import { useAuth0 } from "@auth0/auth0-react";
import loadable from "@loadable/component";
import { compareAsc } from "date-fns";
import i18n from "i18next";
import React, { useContext, useEffect, useState } from "react";
import { initReactI18next } from "react-i18next";
// MIGRATED: Updated imports for React Router v6
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import {
  DefaultToastContainer,
  ToastProvider,
} from "react-toast-notifications";
import { Toaster } from "sonner";
import { user } from "../../__shared/auth";
import { UserContext } from "../../context/user_context";
import { NotificationProvider } from "../../context/NotificationContext";
import { AuthorizedRouteWrapper } from "../AuthorizedRoute/AuthorizedRoute";
import Loading from "../Loading/Loading";
import "./App.scss";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    resources: {
      en: {
        translation: {
          "Welcome to React": "Welcome to React and react-i18next",
        },
      },
    },
    lng: "en",
    keySeparator: false,
    nsSeparator: false,
    // saveMissing: true,
    // missingKeyHandler: function (lng, ns, key, fallbackValue) {
    //   return key;
    // },
    fallbackLng: "en",
    // debug: true,
    interpolation: {
      escapeValue: false,
    },
  });

const MINUTE_MS = 60000;

// lazy loading as another bundle
const LoadingContainer = loadable(() =>
  import(/* webpackPrefetch: true */ "../LoadingContainer/LoadingContainer")
);
const CatalogContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/catalog/components/catalog-container/CatalogContainer"
  )
);
const CoursePublishingContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/course-management/components/course-publishing-container/CoursePublishingContainer"
  )
);
const Curriculum = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/curriculum-management/components/curriculum-container/CurriculumContainer"
  )
);
const DocPageContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/controlled-documents/components/doc-page-container/DocPageContainer"
  )
);
const LearningContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/learning/components/learning-container/LearningContainer"
  )
);
const PDFPopup = loadable(() =>
  import(/* webpackPrefetch: true */ "../PDFPopup/PDFPopup")
);
const TeamContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/my-team/components/TeamContainer"
  )
);
const CourseAssignmentContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/course-assignment/components/course-assignment-container/CourseAssignmentContainer"
  )
);
const AdminCreation = loadable(() =>
  import(/* webpackPrefetch: true */ "@/features/admin-dashboard")
);
const EventContainer = loadable(() =>
  import(/* webpackPrefetch: true */ "../ILTEvents/EventContainer")
);
const AdminCreationContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/admin-dashboard/AdminCreationContainer/AdminCreationContainer"
  )
);
const TestEngineContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/test-engine/components/test-engine-container/TestEngineContainer"
  )
);
const TestTakingView = loadable(() =>
  import(/* webpackPrefetch: true */ "../TestTakingView/TestTakingView")
);
const TestReview = loadable(() =>
  import(/* webpackPrefetch: true */ "../TestReview/TestReview")
);
const ProxyContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/proxy/components/ProxyContainer"
  )
);
const WelcomePage = loadable(() =>
  import(/* webpackPrefetch: true */ "../WelcomePage/WelcomePage")
);
const CertificationManagementContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/certification-management/components/CertificationManagementContainer/CertificationManagementContainer"
  )
);
const CertificationsContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/certifications/components/CertificationsContainer/CertificationsContainer"
  )
);
const TrackAssignContainer = loadable(() =>
  import(
    /* webpackPrefetch: true */ "@/features/track-assign/components/TrackAssignContainer/TrackAssignContainer"
  )
);

const App = () => {
  const [loadError, setLoadError] = useState(undefined);
  const userContext = useContext(UserContext);
  const navigate = useNavigate();
  const isTest =
    localStorage.getItem("playwrightTest") === "true" ? true : false;

  const { user: auth0user, isAuthenticated, logout } = useAuth0();

  function logoutExpiredSession() {
    if (localStorage.getItem("token_exp")) {
      const localLimit = new Date(localStorage.getItem("token_exp"));
      const currentTime = new Date();
      const checkLimit = compareAsc(localLimit, currentTime);

      // console.log("time limit obj: ", {
      //   localLimit: localLimit,
      //   currentTime: currentTime,
      //   checkLimit,
      // });
      if (checkLimit === -1) {
        localStorage.clear();
        logout({
          logoutParams: { returnTo: window.location.origin + "/Welcome" },
        });
      }
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      logoutExpiredSession();
      const interval = setInterval(() => {
        logoutExpiredSession();
      }, MINUTE_MS);

      return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
    }
    return undefined;
  }, [isAuthenticated]);

  const CustomToastContainer = (props) => (
    <DefaultToastContainer
      {...props}
      style={{ zIndex: 10000, overflow: "hidden" }}
    />
  );

  useEffect(() => {
    if (!isAuthenticated && !window.location.pathname.includes("Welcome")) {
      sessionStorage.setItem("returnTo", window.location.pathname);
      navigate("/Welcome", { replace: true });
    }
  }, []);

  if (
    !isTest &&
    !window.location.pathname.includes("Welcome") &&
    (userContext.settingUserData ||
      !auth0user?.["https://auth.appliedmedical.com/identity/userData"])
  ) {
    return (
      <div styleName="loading-container">
        <Loading />
        <div styleName={`button-container`}>
          <p>
            Issues logging in?{" "}
            <span style={{ display: "block" }}>Try logging out </span>
          </p>
          <button
            styleName="logout-btn"
            onClick={() =>
              logout({
                logoutParams: {
                  returnTo: window.location.origin + "/Welcome",
                },
              })
            }
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <NotificationProvider>
      <ToastProvider
        components={{ ToastContainer: CustomToastContainer }}
        placement="top-right"
      >
        {process.env.NODE_ENV === "development" && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
        <Toaster />
        <Routes>
          <Route path="/Welcome" element={<WelcomePage />} />
          <Route
            path="/"
            element={
              <AuthorizedRouteWrapper
                element={
                  user?.source === "AgencyTemps" ? (
                    <Navigate to="/Documents" replace />
                  ) : (
                    <Navigate to="/Learning" replace />
                  )
                }
              />
            }
          />
          <Route
            path="/Documents/:id?"
            element={<AuthorizedRouteWrapper element={<DocPageContainer />} />}
          />
          <Route
            path={`/Learning/:type?/:id?/:isChild?/:childId?`}
            element={
              <AuthorizedRouteWrapper
                element={<LearningContainer title="Training" />}
              />
            }
          />
          <Route
            path={`/Catalog/:type?/:id?/:isChild?/:childId?`}
            element={
              <AuthorizedRouteWrapper
                element={<CatalogContainer title="Catalog" />}
              />
            }
          />
          <Route
            path={`/Coursepublishing/:userfilter/:formprocess`}
            element={
              <AuthorizedRouteWrapper element={<CoursePublishingContainer />} />
            }
          />
          <Route
            path={`/Coursepublishing/search/:userinput`}
            element={
              <AuthorizedRouteWrapper element={<CoursePublishingContainer />} />
            }
          />
          <Route
            path={`/Coursepublishing/:userfilter`}
            element={
              <AuthorizedRouteWrapper element={<CoursePublishingContainer />} />
            }
          />
          <Route
            path={`/Coursepublishing`}
            element={
              <AuthorizedRouteWrapper element={<CoursePublishingContainer />} />
            }
          />

          <Route
            path="/CurriculumManagement/:userfilter/:formprocess"
            element={<AuthorizedRouteWrapper element={<Curriculum />} />}
          />
          <Route
            path="/CurriculumManagement/:userfilter"
            element={<AuthorizedRouteWrapper element={<Curriculum />} />}
          />
          <Route
            path="/CurriculumManagement"
            element={<AuthorizedRouteWrapper element={<Curriculum />} />}
          />
          <Route
            path="/CertificationManagement/*"
            element={
              <AuthorizedRouteWrapper
                element={<CertificationManagementContainer />}
              />
            }
          />
          <Route
            path="/Certifications"
            element={
              <AuthorizedRouteWrapper element={<CertificationsContainer />} />
            }
          />
          <Route
            path="/Team"
            element={<AuthorizedRouteWrapper element={<TeamContainer />} />}
          />

          <Route
            path="/Material/:url"
            element={<AuthorizedRouteWrapper element={<PDFPopup />} />}
          />

          <Route
            path="/InstructorSchedule/:SessionId?"
            element={<AuthorizedRouteWrapper element={<EventContainer />} />}
          />

          <Route
            path="/CourseAssignment"
            element={
              <AuthorizedRouteWrapper element={<CourseAssignmentContainer />} />
            }
          />
          <Route
            path="/Admin"
            element={<AuthorizedRouteWrapper element={<AdminCreation />} />}
          />
          <Route
            path="/Admin/CustomGroups"
            element={
              <AuthorizedRouteWrapper element={<AdminCreationContainer />} />
            }
          />
          <Route
            path="/Admin/UserTranscripts/:tab"
            element={
              <AuthorizedRouteWrapper element={<AdminCreationContainer />} />
            }
          />
          <Route
            path="/Admin/:page"
            element={
              <AuthorizedRouteWrapper element={<AdminCreationContainer />} />
            }
          />
          <Route
            path="/Testengine"
            element={
              <AuthorizedRouteWrapper element={<TestEngineContainer />} />
            }
          />
          <Route
            path="/TestTakingView/:examId/:assignmentId"
            element={<AuthorizedRouteWrapper element={<TestTakingView />} />}
          />

          <Route
            path="/TestReview/:examId?/:assignmentId?"
            element={<AuthorizedRouteWrapper element={<TestReview />} />}
          />
          {/* MIGRATED: review - render prop requires manual conversion to element */}
          <Route
            path="/TestSession/:examId/:id"
            element={
              <AuthorizedRouteWrapper
                element={<LoadingContainer loadError={true} />}
              />
            }
          />
          <Route
            path="/Proxy"
            element={<AuthorizedRouteWrapper element={<ProxyContainer />} />}
          />
          <Route
            path="/TrackAndAssign"
            element={
              <AuthorizedRouteWrapper element={<TrackAssignContainer />} />
            }
          />
          <Route path="/login" element={<Navigate to="/" replace />} />

          <Route
            path="*"
            element={
              <LoadingContainer
                loadError={loadError}
                setLoadError={setLoadError}
              />
            }
          />
        </Routes>
      </ToastProvider>
    </NotificationProvider>
  );
};

export default App;
